import { parseDate } from '@internationalized/date';
import * as stylex from '@stylexjs/stylex';
import { useForm, useStore } from '@tanstack/react-form';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useId } from 'react';
import {
  type DateValue,
  Group,
  ListLayout,
  ToggleButtonGroup,
  Virtualizer,
} from 'react-aria-components';
import { pipe as rPipe, map as rMap, filter as rFilter } from 'remeda';

import { ListBoxNoSearchResults } from '@/components/list-box-no-search-results.component';
import { useMinDelay } from '@/hooks/use-min-delay.hook';
import { CampaignRequestStatusEnum, CampaignRequestTargetingTypeEnum } from '@/libs/openapi';
import {
  CALENDAR_SLOTS,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  Input,
  ToggleButton,
  View,
  Text,
  NumberField,
  Label,
  ComboBox,
  Popover,
  ListBox,
  ListBoxItem,
  ListLoadingState,
  DialogBar,
  DIALOG_SLOTS,
  Button,
  Form,
  FORM_SLOTS,
  ProgressIndicator,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  TextButton,
  LIST_BOX_ITEM_SLOTS,
} from '@/libs/serenity-ui';
import { useDebounce } from '@/libs/serenity-ui/hooks/use-debounce/use-debounce.hook';
import { campaignRequestProgramQueries } from '@/resources/campaign-request-program/campaign-request-program.api';
import { organizationTeamQueries } from '@/resources/organization-team/organization-team.api';
import { organizationUserQueries } from '@/resources/organization-user/organization-user.api';

interface FiltersFormProps {
  onClose?: () => void;
  from: '/_main/_list/campaigns/' | '/_main/_list/campaigns/archive' | '/_main/_list/programs/$programId/campaigns';
}

interface CampaignFiltersFormValues {
  id: number | undefined;
  status: number[];
  hasNewComments: boolean | undefined;
  hasPendingEdits: boolean | undefined;
  totalBudgetGte: number | undefined;
  totalBudgetLte: number | undefined;
  creativeType: number | undefined;
  targetingType: number | undefined;
  ownerId: string | undefined;
  teamId: number | undefined;
  programId: number | undefined;
  startDateGte: string | undefined;
  endDateLte: string | undefined;
  ownerSearchInput: string;
  teamSearchInput: string;
  programSearchInput: string;
}

const META_FIELDS = new Set([ 'ownerSearchInput', 'teamSearchInput', 'programSearchInput' ]);

export function CampaignRequestsFiltersForm({ onClose, from }: FiltersFormProps) {
  const id = useId();
  const search = useSearch({ from });
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      id: undefined,
      status: [],
      hasNewComments: undefined,
      hasPendingEdits: undefined,
      totalBudgetGte: undefined,
      totalBudgetLte: undefined,
      creativeType: undefined,
      targetingType: undefined,
      ownerId: undefined,
      teamId: undefined,
      programId: undefined,
      startDateGte: undefined,
      endDateLte: undefined,
      ownerSearchInput: '',
      teamSearchInput: '',
      programSearchInput: '',
      ...Object.fromEntries(search.columnFilters.map(({ id: filterId, value }) => [ filterId, value ])),
    } as CampaignFiltersFormValues,
    onSubmit: () => {
      navigate({
        to: '.',
        search: (prev) => ({
          sorting: prev.sorting,
          columnFilters: rPipe(
            Object.entries(form.state.values),
            rFilter(([ key, value ]) => !META_FIELDS.has(key) && value != null && !(Array.isArray(value) && value.length === 0)),
            rMap(([ filterId, value ]) => ({ id: filterId, value })),
          ),
          pagination: { pageIndex: 0, pageSize: prev.pagination?.pageSize ?? 100 },
        }),
      });
      onClose?.();
    },
  });

  const ownerId = useStore(form.store, (state) => state.values.ownerId);
  const teamId = useStore(form.store, (state) => state.values.teamId);
  const programId = useStore(form.store, (state) => state.values.programId);
  const ownerSearchInput = useStore(form.store, (state) => state.values.ownerSearchInput);
  const teamSearchInput = useStore(form.store, (state) => state.values.teamSearchInput);
  const programSearchInput = useStore(form.store, (state) => state.values.programSearchInput);

  const debouncedOwnerSearch = useDebounce(ownerSearchInput, PROGRESS_INDICATOR_MIN_DELAY_DURATION);
  const debouncedTeamSearch = useDebounce(teamSearchInput, PROGRESS_INDICATOR_MIN_DELAY_DURATION);
  const debouncedProgramSearch = useDebounce(programSearchInput, PROGRESS_INDICATOR_MIN_DELAY_DURATION);

  const usersQuery = useQuery({
    ...organizationUserQueries.list({
      limit: 5,
      sortBy: [ 'firstName:ASC' ],
      ...debouncedOwnerSearch && !ownerId ? { email: debouncedOwnerSearch } : {},
    }),
    placeholderData: keepPreviousData,
  });
  const teamsQuery = useQuery({
    ...organizationTeamQueries.list({
      limit: 5,
      sortBy: [ 'name:ASC' ],
      ...debouncedTeamSearch && !teamId ? { name: debouncedTeamSearch } : {},
    }),
    placeholderData: keepPreviousData,
  });
  const programsQuery = useQuery({
    ...campaignRequestProgramQueries.list({
      paging: { pageIndex: 0, pageSize: 5 },
      filter: debouncedProgramSearch && !programId ? { name: debouncedProgramSearch } : undefined,
    }),
    placeholderData: keepPreviousData,
  });

  const usersDelayed = useMinDelay(usersQuery.isFetching, { data: usersQuery.data, isError: usersQuery.isError }, PROGRESS_INDICATOR_MIN_DELAY_DURATION);
  const teamsDelayed = useMinDelay(teamsQuery.isFetching, { data: teamsQuery.data, isError: teamsQuery.isError }, PROGRESS_INDICATOR_MIN_DELAY_DURATION);
  const programsDelayed = useMinDelay(
    programsQuery.isFetching,
    { data: programsQuery.data, isError: programsQuery.isError },
    PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  );

  const ownerInputValue = ownerSearchInput || (ownerId ? usersQuery.data?.data?.find((item) => item.original.id === ownerId)?.meta.fullName ?? '' : '');
  const teamInputValue = teamSearchInput || (teamId ? teamsQuery.data?.data?.find((item) => item.original.id === teamId)?.original.name ?? '' : '');
  const programInputValue = programSearchInput || (programId ? programsQuery.data?.data?.find((item) => item.original.id === programId)?.original.name ?? '' : '');

  return (
    <>
      <Form
        id={id}
        style={formStyles.base}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}>
        {(renderProps) => (
          <>
            <form.Field name="id">
              {(field) => (
                <View style={renderProps.fieldStyles}>
                  <Text color={renderProps.labelColor} id={`${id}-campaign-id`} size={16}>ID</Text>
                  <NumberField
                    id={field.name}
                    minValue={1}
                    name={field.name}
                    size={32}
                    value={field.state.value ?? NaN}
                    onChange={(value) => field.handleChange(isNaN(value) ? undefined : value)}>
                    <Label hidden>ID</Label>
                    <Input />
                  </NumberField>
                </View>
              )}
            </form.Field>

            <form.Field name="status">
              {(field) => (
                <View style={renderProps.fieldStyles}>
                  <Text color={renderProps.labelColor} id={`${id}-status`} size={16}>Status</Text>
                  <ToggleButtonGroup
                    aria-labelledby={`${id}-status`}
                    selectedKeys={new Set(field.state.value.map(String))}
                    selectionMode="multiple"
                    onSelectionChange={(keys) => field.handleChange([ ...keys ].map(Number))}
                    {...stylex.props(toggleButtonGroupStyles.base)}>
                    <ToggleButton id={String(CampaignRequestStatusEnum.DRAFT)} size={32} variant="outlined">Draft</ToggleButton>
                    <ToggleButton id={String(CampaignRequestStatusEnum.IN_REVIEW)} size={32} variant="outlined">In review</ToggleButton>
                    <ToggleButton id={String(CampaignRequestStatusEnum.PROCESSING)} size={32} variant="outlined">Processing</ToggleButton>
                    <ToggleButton id={String(CampaignRequestStatusEnum.ACTIVE)} size={32} variant="outlined">Active</ToggleButton>
                    <ToggleButton id={String(CampaignRequestStatusEnum.PAUSED)} size={32} variant="outlined">Paused</ToggleButton>
                    <ToggleButton id={String(CampaignRequestStatusEnum.ENDED)} size={32} variant="outlined">Ended</ToggleButton>
                    <ToggleButton id={String(CampaignRequestStatusEnum.COMPLETED)} size={32} variant="outlined">Completed</ToggleButton>
                    <ToggleButton id={String(CampaignRequestStatusEnum.DECLINED)} size={32} variant="outlined">Declined</ToggleButton>
                  </ToggleButtonGroup>
                </View>
              )}
            </form.Field>

            <form.Field name="hasNewComments">
              {(field) => (
                <View style={renderProps.fieldStyles}>
                  <Text color={renderProps.labelColor} id={`${id}-new-comments`} size={16}>New comments</Text>
                  <ToggleButtonGroup
                    aria-labelledby={`${id}-new-comments`}
                    selectedKeys={field.state.value != null ? new Set([ String(field.state.value) ]) : new Set()}
                    selectionMode="single"
                    onSelectionChange={(keys) => {
                      const selected = [ ...keys ];
                      field.handleChange(selected.length > 0 ? selected[0] === 'true' : undefined);
                    }}
                    {...stylex.props(toggleButtonGroupStyles.base)}>
                    <ToggleButton id="true" size={32} variant="outlined">Yes</ToggleButton>
                    <ToggleButton id="false" size={32} variant="outlined">No</ToggleButton>
                  </ToggleButtonGroup>
                </View>
              )}
            </form.Field>

            <form.Field name="hasPendingEdits">
              {(field) => (
                <View style={renderProps.fieldStyles}>
                  <Text color={renderProps.labelColor} id={`${id}-new-updates`} size={16}>Pending edit requests</Text>
                  <ToggleButtonGroup
                    aria-labelledby={`${id}-new-updates`}
                    selectedKeys={field.state.value != null ? new Set([ String(field.state.value) ]) : new Set()}
                    selectionMode="single"
                    onSelectionChange={(keys) => {
                      const selected = [ ...keys ];
                      field.handleChange(selected.length > 0 ? selected[0] === 'true' : undefined);
                    }}
                    {...stylex.props(toggleButtonGroupStyles.base)}>
                    <ToggleButton id="true" size={32} variant="outlined">Yes</ToggleButton>
                    <ToggleButton id="false" size={32} variant="outlined">No</ToggleButton>
                  </ToggleButtonGroup>
                </View>
              )}
            </form.Field>

            <Group aria-labelledby={`${id}-budget`} {...stylex.props(renderProps.fieldStyles)}>
              <Text color={renderProps.labelColor} id={`${id}-budget`} size={16}>Budget</Text>
              <View style={renderProps.rowViewStyle}>
                <form.Field name="totalBudgetGte">
                  {(field) => (
                    <NumberField
                      id={field.name}
                      name={field.name}
                      size={32}
                      value={field.state.value ?? NaN}
                      onChange={(value) => field.handleChange(isNaN(value) ? undefined : value)}>
                      <Label hidden>From</Label>
                      <Input placeholder="From">
                        {(inputRenderProps) =>
                          <>
                            {inputRenderProps.defaultChildren}
                            <Text color="disabled">USD</Text>
                          </>}
                      </Input>
                    </NumberField>
                  )}
                </form.Field>

                <form.Field name="totalBudgetLte">
                  {(field) => (
                    <NumberField
                      id={field.name}
                      name={field.name}
                      size={32}
                      value={field.state.value ?? NaN}
                      onChange={(value) => field.handleChange(isNaN(value) ? undefined : value)}>
                      <Label hidden>To</Label>
                      <Input placeholder="To">
                        {(inputRenderProps) =>
                          <>
                            {inputRenderProps.defaultChildren}
                            <Text color="disabled">USD</Text>
                          </>}
                      </Input>
                    </NumberField>
                  )}
                </form.Field>
              </View>
            </Group>

            <form.Field name="startDateGte">
              {(field) => (
                <DatePicker
                  granularity="day"
                  id={field.name}
                  style={renderProps.fieldStyles}
                  value={field.state.value ? parseDate(field.state.value) as unknown as DateValue : null}
                  onBlur={field.handleBlur}
                  onChange={(value) => {
                    field.handleChange(value ? value.toString().substring(0, 10) : undefined);
                  }}>
                  <Label color={renderProps.labelColor}>Start date</Label>
                  <DateInput size={32} style={dateInputStyles.base} />
                  <Popover style={popoverStyles.base}>
                    <Calendar>
                      <View slot={CALENDAR_SLOTS.CALENDARS_VIEW}>
                        <CalendarHeader isSmall />
                      </View>
                      <View slot={CALENDAR_SLOTS.CALENDARS_VIEW}>
                        <CalendarGrid weekdayStyle="short">
                          <CalendarGridHeader>
                            {(day) => <CalendarHeaderCell isSmall>{day}</CalendarHeaderCell>}
                          </CalendarGridHeader>
                          <CalendarGridBody>
                            {(date) => <CalendarCell date={date} isSmall />}
                          </CalendarGridBody>
                        </CalendarGrid>
                      </View>
                    </Calendar>
                  </Popover>
                </DatePicker>
              )}
            </form.Field>

            <form.Field name="endDateLte">
              {(field) => (
                <DatePicker
                  granularity="day"
                  id={field.name}
                  style={renderProps.fieldStyles}
                  value={field.state.value ? parseDate(field.state.value) as unknown as DateValue : null}
                  onBlur={field.handleBlur}
                  onChange={(value) => {
                    field.handleChange(value ? value.toString().substring(0, 10) : undefined);
                  }}>
                  <Label color={renderProps.labelColor}>End date</Label>
                  <DateInput size={32} style={dateInputStyles.base} />
                  <Popover style={popoverStyles.base}>
                    <Calendar>
                      <View slot={CALENDAR_SLOTS.CALENDARS_VIEW}>
                        <CalendarHeader isSmall />
                      </View>
                      <View slot={CALENDAR_SLOTS.CALENDARS_VIEW}>
                        <CalendarGrid weekdayStyle="short">
                          <CalendarGridHeader>
                            {(day) => <CalendarHeaderCell isSmall>{day}</CalendarHeaderCell>}
                          </CalendarGridHeader>
                          <CalendarGridBody>
                            {(date) => <CalendarCell date={date} isSmall />}
                          </CalendarGridBody>
                        </CalendarGrid>
                      </View>
                    </Calendar>
                  </Popover>
                </DatePicker>
              )}
            </form.Field>

            <form.Field name="targetingType">
              {(field) => (
                <View style={renderProps.fieldStyles}>
                  <Text color={renderProps.labelColor} id={`${id}-targeting-type`} size={16}>Targeting type</Text>
                  <ToggleButtonGroup
                    aria-labelledby={`${id}-targeting-type`}
                    selectedKeys={field.state.value != null ? new Set([ String(field.state.value) ]) : new Set()}
                    selectionMode="single"
                    onSelectionChange={(keys) => {
                      const selected = [ ...keys ];
                      field.handleChange(selected.length > 0 ? Number(selected[0]) : undefined);
                    }}
                    {...stylex.props(toggleButtonGroupStyles.base)}>
                    <ToggleButton id={String(CampaignRequestTargetingTypeEnum.NPI)} size={32} variant="outlined">NPI</ToggleButton>
                    <ToggleButton id={String(CampaignRequestTargetingTypeEnum.GEO)} size={32} variant="outlined">Geo</ToggleButton>
                    <ToggleButton id={String(CampaignRequestTargetingTypeEnum.SPECIALTY)} size={32} variant="outlined">Specialty</ToggleButton>
                    <ToggleButton id={String(CampaignRequestTargetingTypeEnum.SPECIALTY_AUTH)} size={32} variant="outlined">Specialty-auth</ToggleButton>
                    <ToggleButton id={String(CampaignRequestTargetingTypeEnum.RON)} size={32} variant="outlined">RoN</ToggleButton>
                  </ToggleButtonGroup>
                </View>
              )}
            </form.Field>

            <form.Field name="ownerId">
              {(field) => (
                <View slot={FORM_SLOTS.FIELD_VIEW}>
                  <ComboBox
                    allowsEmptyCollection
                    id={field.name}
                    inputValue={ownerInputValue}
                    isReadOnly={usersDelayed.data.isError}
                    name={field.name}
                    selectedKey={field.state.value ?? null}
                    style={renderProps.fieldStyles}
                    onInputChange={(value) => {
                      field.form.setFieldValue('ownerSearchInput', value);
                      field.handleChange(undefined);
                    }}
                    onSelectionChange={(key) => {
                      field.handleChange(key != null ? String(key) : undefined);
                      const selected = usersQuery.data?.data?.find((item) => item.original.id === String(key));
                      field.form.setFieldValue('ownerSearchInput', selected?.meta.fullName ?? '');
                    }}>
                    {({ isOpen }) => (
                      <>
                        <Label color={renderProps.labelColor}>Owner</Label>
                        <Input placeholder="Select" size={32}>
                          {!isOpen && usersDelayed.pending
                            ? (inputRenderProps) => (
                              <>
                                {inputRenderProps.defaultChildren}
                                <ProgressIndicator />
                              </>
                            ) : undefined}
                        </Input>
                        <Popover>
                          <Virtualizer
                            layout={ListLayout}
                            layoutOptions={{
                              rowHeight: 40,
                              padding: 0,
                              dropIndicatorThickness: 0,
                              gap: 0,
                            }}>
                            <ListBox
                              items={usersDelayed.data.data?.data ?? []}
                              renderEmptyState={() => (usersDelayed.pending ? <ListLoadingState /> : <ListBoxNoSearchResults />)}>
                              {(item) =>
                                <ListBoxItem
                                  id={item.original.id}
                                  size={40}
                                  textValue={item.meta.fullName}>
                                  <Text slot={LIST_BOX_ITEM_SLOTS.LABEL}>{item.meta.fullName}</Text>
                                  <Text slot={LIST_BOX_ITEM_SLOTS.DESCRIPTION}>{item.original.email}</Text>
                                </ListBoxItem>}
                            </ListBox>
                          </Virtualizer>
                        </Popover>
                      </>
                    )}
                  </ComboBox>
                  {usersDelayed.data.isError &&
                    <TextButton
                      isDisabled={usersDelayed.pending}
                      size={40}
                      slot={FORM_SLOTS.RETRY_TEXT_BUTTON}
                      style={[ retryButtonStyles.small ]}
                      onPress={() => usersQuery.refetch()}
                    />}
                </View>
              )}
            </form.Field>

            <form.Field name="teamId">
              {(field) => (
                <View slot={FORM_SLOTS.FIELD_VIEW}>
                  <ComboBox
                    allowsEmptyCollection
                    id={field.name}
                    inputValue={teamInputValue}
                    isReadOnly={teamsDelayed.data.isError}
                    name={field.name}
                    selectedKey={field.state.value ?? null}
                    style={renderProps.fieldStyles}
                    onInputChange={(value) => {
                      field.form.setFieldValue('teamSearchInput', value);
                      field.handleChange(undefined);
                    }}
                    onSelectionChange={(key) => {
                      field.handleChange(key != null ? Number(key) : undefined);
                      const selected = teamsQuery.data?.data?.find((item) => item.original.id === Number(key));
                      field.form.setFieldValue('teamSearchInput', selected?.original.name ?? '');
                    }}>
                    {({ isOpen }) => (
                      <>
                        <Label color={renderProps.labelColor}>Team</Label>
                        <Input placeholder="Select" size={32}>
                          {!isOpen && teamsDelayed.pending
                            ? (inputRenderProps) => (
                              <>
                                {inputRenderProps.defaultChildren}
                                <ProgressIndicator />
                              </>
                            ) : undefined}
                        </Input>
                        <Popover>
                          <Virtualizer
                            layout={ListLayout}
                            layoutOptions={{
                              rowHeight: 40,
                              padding: 0,
                              dropIndicatorThickness: 0,
                              gap: 0,
                            }}>
                            <ListBox
                              items={teamsDelayed.data.data?.data ?? []}
                              renderEmptyState={() => (teamsDelayed.pending ? <ListLoadingState /> : <ListBoxNoSearchResults />)}>
                              {(item) =>
                                <ListBoxItem
                                  id={item.original.id}
                                  size={40}
                                  textValue={item.original.name}>
                                  {item.original.name}
                                </ListBoxItem>}
                            </ListBox>
                          </Virtualizer>
                        </Popover>
                      </>
                    )}
                  </ComboBox>
                  {teamsDelayed.data.isError &&
                    <TextButton
                      isDisabled={teamsDelayed.pending}
                      size={40}
                      slot={FORM_SLOTS.RETRY_TEXT_BUTTON}
                      style={[ retryButtonStyles.small ]}
                      onPress={() => teamsQuery.refetch()}
                    />}
                </View>
              )}
            </form.Field>

            {from !== '/_main/_list/programs/$programId/campaigns' &&
            <form.Field name="programId">
              {(field) => (
                <View slot={FORM_SLOTS.FIELD_VIEW}>
                  <ComboBox
                    allowsEmptyCollection
                    id={field.name}
                    inputValue={programInputValue}
                    isReadOnly={programsDelayed.data.isError}
                    name={field.name}
                    selectedKey={field.state.value ?? null}
                    style={renderProps.fieldStyles}
                    onInputChange={(value) => {
                      field.form.setFieldValue('programSearchInput', value);
                      field.handleChange(undefined);
                    }}
                    onSelectionChange={(key) => {
                      field.handleChange(key != null ? Number(key) : undefined);
                      const selected = programsQuery.data?.data?.find((item) => item.original.id === Number(key));
                      field.form.setFieldValue('programSearchInput', selected?.original.name ?? '');
                    }}>
                    {({ isOpen }) => (
                      <>
                        <Label color={renderProps.labelColor}>Program</Label>
                        <Input placeholder="Select" size={32}>
                          {!isOpen && programsDelayed.pending
                            ? (inputRenderProps) => (
                              <>
                                {inputRenderProps.defaultChildren}
                                <ProgressIndicator />
                              </>
                            ) : undefined}
                        </Input>
                        <Popover>
                          <Virtualizer
                            layout={ListLayout}
                            layoutOptions={{
                              rowHeight: 40,
                              padding: 0,
                              dropIndicatorThickness: 0,
                              gap: 0,
                            }}>
                            <ListBox
                              items={programsDelayed.data.data?.data ?? []}
                              renderEmptyState={() => (programsDelayed.pending ? <ListLoadingState /> : <ListBoxNoSearchResults />)}>
                              {(item) =>
                                <ListBoxItem
                                  id={item.original.id}
                                  size={40}
                                  textValue={item.original.name}>
                                  {item.original.name}
                                </ListBoxItem>}
                            </ListBox>
                          </Virtualizer>
                        </Popover>
                      </>
                    )}
                  </ComboBox>
                  {programsDelayed.data.isError &&
                    <TextButton
                      isDisabled={programsDelayed.pending}
                      size={40}
                      slot={FORM_SLOTS.RETRY_TEXT_BUTTON}
                      style={[ retryButtonStyles.small ]}
                      onPress={() => programsQuery.refetch()}
                    />}
                </View>
              )}
            </form.Field>}
          </>
        )}
      </Form>
      <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
        <Button
          isRounded
          size={40}
          variant="ghost"
          onPress={() => {
            form.reset();
            navigate({
              to: '.',
              search: (prev) => ({
                sorting: prev.sorting,
                columnFilters: [],
                pagination: { pageIndex: 0, pageSize: prev.pagination?.pageSize ?? 100 },
              }),
            });
          }}>
            Clear all
        </Button>
        <Button form={id} isRounded size={40} type="submit">Apply</Button>
      </DialogBar>
    </>
  );
}

const formStyles = stylex.create({
  base: {
    overflow: 'auto',
    paddingBlock: 8,
    scrollbarWidth: 'none',
  },
});

const toggleButtonGroupStyles = stylex.create({
  base: {
    alignContent: 'center',
    alignItems: 'center',
    display: 'flex',
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 0,
    flexWrap: 'wrap',
    gap: 12,
  },
});

const retryButtonStyles = stylex.create({
  small: {
    height: 36,
  },
});

const dateInputStyles = stylex.create({
  base: {
    width: 'calc(100% - 1px)',
  },
});

const popoverStyles = stylex.create({
  base: {
    minWidth: 0,
  },
});
